import { useEffect, useId, useRef, useState } from "react";
import type { GhlWriteCounts, GhlWriterStatus } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { DataTable } from "../design/components/DataTable";
import { Reason } from "../design/components/Reason";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { useAccess } from "../session/access";
import { whenParts } from "./close-import-format";
import { ghlWriteQuery, useGhlWriteActions } from "./ghl-read-queries";

// La scrittura su GoHighLevel nella pagina «GoHighLevel» (INGR-19; campi e regole: scelta di Claude 29/10/2026), soltanto per il Super
// admin di Gioia365: che cosa si scrive, gli ambiti che il token deve avere, 1. «Prova senza scrivere» (numeri ed esempi mascherati),
// 2. l'interruttore (spento di partenza; si accende soltanto dopo una prova riuscita con la chiave di adesso, con una conferma) e «Crea i
// contatti che mancano» (spento di partenza), l'ultimo giro e la coda.

/** Il motivo di un esempio che non si scrive, fra quelli che la pagina sa dire. */
const skipReasonOf = (reason: string | null) => (reason === "ambiguous" || reason === "archived" ? reason : "noMatch");

/** Quanti Lead prova «Prova senza scrivere». */
const SAMPLE = 20;

export function GhlWritePart({ connected }: { connected: boolean }) {
  const t = useT();
  const tab = useTab();
  const titleId = useId();
  const query = useQuery(ghlWriteQuery(tab));
  const data = query.data;
  return (
    <section className="g-ci__nightly-part" aria-labelledby={titleId}>
      <h3 id={titleId}>{t("ghlWrite.title")}</h3>
      <p className="g-ci__muted">{t("ghlWrite.intro")}</p>
      {data ? (
        <>
          <p className="g-ci__muted">{t("ghlWrite.fields", { fields: data.fields.join(", "), tags: data.tags.join(", ") })}</p>
          <p className="g-ci__muted">{t("ghlWrite.scopes", { scopes: data.requiredScopes.join(", ") })}</p>
          {data.stoppedReason ? <Reason tone="warning">{t(`ghlWrite.stopped.${data.stoppedReason}`, { scopes: data.requiredScopes.join(", ") })}</Reason> : null}
          {connected ? (
            <>
              <DryRunPart data={data} />
              {/* Lo stato salvato cambia: la parte riparte da quello (la spunta di «Crea i contatti che mancano»). */}
              <SwitchPart key={`${data.enabled}|${data.createMissing}`} data={data} />
            </>
          ) : (
            <p>{t("ghlWrite.needsKey")}</p>
          )}
        </>
      ) : query.isError ? (
        <Reason tone="warning">{messageForError(t, query.error)}</Reason>
      ) : (
        <p className="g-ci__muted">{t("ghlWrite.loading")}</p>
      )}
    </section>
  );
}

function Counts({ counts }: { counts: GhlWriteCounts }) {
  const t = useT();
  const { locale } = useLocale();
  const keys = ["considered", "update", "create", "unchanged", "noMatch", "ambiguous", "rejected", "archived", "failed"] as const;
  return (
    <ul className="g-ci__ghl-counts">
      {keys.map((key) => (
        <li key={key}>{t(`ghlWrite.counts.${key}`, { count: formatCount(locale, counts[key]) })}</li>
      ))}
    </ul>
  );
}

function DryRunPart({ data }: { data: GhlWriterStatus }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const access = useAccess();
  const actions = useGhlWriteActions(tab);
  const dry = data.lastDryRun;
  const start = () =>
    actions.dryRun.mutate(
      { sample: SAMPLE },
      {
        onSuccess: () => toast.show(t("ghlWrite.dryRunToast"), "success"),
        onError: (error) => toast.show(messageForError(t, error), "warning"),
      },
    );
  return (
    <div className="g-ci__nightly-part">
      <h4>{t("ghlWrite.dryRunTitle")}</h4>
      <p className="g-ci__muted">{t("ghlWrite.dryRunText", { count: String(SAMPLE) })}</p>
      {dry ? (
        <>
          <p className="g-ci__muted">{dry.by ? t("ghlWrite.dryRunWhen", { ...whenParts(access.context.settings.timeZone, dry.at), person: dry.by }) : t("ghlWrite.dryRunWhenNoOne", whenParts(access.context.settings.timeZone, dry.at))}</p>
          {!dry.current ? <Reason tone="warning">{t("ghlWrite.dryRunOld")}</Reason> : null}
          {dry.outcome !== "ok" ? <Reason tone="warning">{t(`ghlWrite.outcome.${dry.outcome}`, { scopes: data.requiredScopes.join(", ") })}</Reason> : null}
          <Counts counts={dry.counts} />
          {dry.missingFields.length > 0 ? <p className="g-ci__muted">{t("ghlWrite.missingFields", { fields: dry.missingFields.join(", ") })}</p> : null}
          {dry.examples.length > 0 ? (
            <div className="g-ci__table g-ci__table--vars">
              <DataTable caption={t("ghlWrite.examplesTitle")}>
                <thead>
                  <tr>
                    <th scope="col">{t("ghlWrite.exampleLead")}</th>
                    <th scope="col">{t("ghlWrite.exampleWhat")}</th>
                    <th scope="col">{t("ghlWrite.exampleFields")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dry.examples.map((example, index) => (
                    <tr key={index}>
                      <td>{[example.name, example.email, example.phone].filter(Boolean).join(" · ")}</td>
                      <td>
                        {example.operation === "skip"
                          ? t(`ghlWrite.skip.${skipReasonOf(example.reason)}`)
                          : t(`ghlWrite.operation.${example.operation}`, { by: example.matchedBy ? t(`ghlWrite.matchedBy.${example.matchedBy}`) : "" })}
                      </td>
                      <td>
                        {Object.entries(example.fields)
                          .filter(([, value]) => value !== "")
                          .map(([name, value]) => `${name}: ${value}`)
                          .concat(example.tags)
                          .join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </div>
          ) : null}
        </>
      ) : (
        <p>{t("ghlWrite.noDryRun")}</p>
      )}
      <div className="g-ci__nightly-actions">
        <Button onClick={start} disabled={actions.dryRun.isPending}>
          {t(actions.dryRun.isPending ? "ghlWrite.dryRunRunning" : "ghlWrite.dryRunStart")}
        </Button>
      </div>
    </div>
  );
}

function SwitchPart({ data }: { data: GhlWriterStatus }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const access = useAccess();
  const { locale } = useLocale();
  const actions = useGhlWriteActions(tab);
  const checkboxId = useId();
  const [createMissing, setCreateMissing] = useState(data.createMissing);
  const [confirming, setConfirming] = useState(false);
  const yes = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (confirming) yes.current?.focus();
  }, [confirming]);
  const save = (enabled: boolean) =>
    actions.save.mutate(
      { enabled, createMissing },
      {
        onSuccess: () => {
          setConfirming(false);
          toast.show(t(enabled ? "ghlWrite.onToast" : "ghlWrite.offToast"), "success");
        },
        onError: (error) => toast.show(messageForError(t, error), "warning"),
      },
    );
  const canTurnOn = data.lastDryRun?.current === true && data.lastDryRun.outcome === "ok";
  const round = data.lastRound;
  const timeZone = access.context.settings.timeZone;
  return (
    <div className="g-ci__nightly-part">
      <h4>{t("ghlWrite.switchTitle")}</h4>
      <p>{t(data.enabled ? "ghlWrite.isOn" : "ghlWrite.isOff")}</p>
      {data.changed ? <p className="g-ci__muted">{data.changed.by ? t("ghlWrite.changed", { ...whenParts(timeZone, data.changed.at), person: data.changed.by }) : t("ghlWrite.changedNoOne", whenParts(timeZone, data.changed.at))}</p> : null}
      <Checkbox id={checkboxId} label={t("ghlWrite.createMissing")} hint={t("ghlWrite.createMissingHint")} checked={createMissing} onChange={setCreateMissing} disabled={actions.save.isPending} />
      {!data.enabled && !canTurnOn ? <p>{t("ghlWrite.needsDryRun")}</p> : null}
      {confirming ? (
        <div className="g-ci__nightly-confirm" role="group" aria-label={t("ghlWrite.turnOn")}>
          <p>{t(createMissing ? "ghlWrite.confirmWithCreate" : "ghlWrite.confirm")}</p>
          <div className="g-ci__nightly-actions">
            <Button ref={yes} variant="primary" onClick={() => save(true)} disabled={actions.save.isPending}>
              {t("ghlWrite.turnOnYes")}
            </Button>
            <Button onClick={() => setConfirming(false)}>{t("common.cancel")}</Button>
          </div>
        </div>
      ) : (
        <div className="g-ci__nightly-actions">
          {data.enabled ? (
            <>
              <Button onClick={() => save(true)} disabled={actions.save.isPending || createMissing === data.createMissing}>
                {t("ghlWrite.saveSetting")}
              </Button>
              <Button onClick={() => save(false)} disabled={actions.save.isPending}>
                {t("ghlWrite.turnOff")}
              </Button>
            </>
          ) : (
            <Button variant="primary" onClick={() => setConfirming(true)} disabled={!canTurnOn || actions.save.isPending}>
              {t("ghlWrite.turnOn")}
            </Button>
          )}
        </div>
      )}
      <h5>{t("ghlWrite.roundTitle")}</h5>
      {round ? (
        <>
          <p className="g-ci__muted">{t("ghlWrite.roundWhen", whenParts(timeZone, round.at))}</p>
          {round.outcome !== "ok" ? <Reason tone="warning">{t(`ghlWrite.outcome.${round.outcome}`, { scopes: data.requiredScopes.join(", ") })}</Reason> : null}
          <Counts counts={round.counts} />
        </>
      ) : (
        <p>{t("ghlWrite.noRound")}</p>
      )}
      <p className="g-ci__muted">
        {t("ghlWrite.queue", {
          pending: formatCount(locale, data.queue.pending),
          done: formatCount(locale, data.queue.done),
          skipped: formatCount(locale, data.queue.skipped),
          failed: formatCount(locale, data.queue.failed),
        })}
      </p>
    </div>
  );
}
